import {
  Box,
  Flex,
  HStack,
  Heading,
  IconButton,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import {
  ContentNodeUtils,
  DataTypeConfigUtils,
  DataTypeLocator,
  FieldContentNode,
  InputObjectDataTypeConfig,
  InputValueContentNode,
  ObjectContentNode,
  QueryNodeUtils,
  RuntimeOperations,
} from '@helo/cms'
import { Assert, Record, Value, pipe } from '@helo/utils'
import {
  DefaultMenuItem,
  GetContextMenuItems,
  MenuItemDef,
} from 'ag-grid-enterprise'
import { AgGridReact, AgGridReactProps } from 'ag-grid-react'
import assert from 'assert'
import { isEqual } from 'lodash'
import { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import { RuntimeOperationBaseContext } from '../../../../../packages/cms/dist/esm/StateOperations/runtime-processing.parts'
import { ErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary'
import { WidgetError } from '../../components/Errors/WidgetError'
import { ExportModal } from '../../components/ExportModal/ExportModal'
import { ColumnFilterEditor } from '../../components/FilterEditor/ColumnFilterEditor'
import { FilterEditor } from '../../components/FilterEditor/FilterEditor'
import {
  type LoadOptionsForPickListParams,
  getAvailableFilterFieldsDataTypeConfigs,
  getFilterObjectContentNode,
  getUnmappedTypeName,
} from '../../components/FilterEditor/FilterEditor/FilterEditor.parts'
import { Icon } from '../../components/Icons/Icon'
import { SaveIcon } from '../../components/Icons/SaveIcon'
import { SaveViewModal } from '../../components/SaveViewModal/SaveViewModal'
import { InlineSkeleton, Skeleton } from '../../components/SkeletonLoader'
import { Table } from '../../components/Table'
import type { RenderColumnMenu } from '../../components/Table/TableFilterHeader'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { numberFormatter } from '../../datatypes/numberFormatter'
import { useOnChange } from '../../hooks/useOnChange'
import { CubeDistinctValues } from '../../services/cubeDistinctValuesQuery'
import { createDistinctValuesLoadOptions } from '../../services/distinctValuesQuery'
import { useAppStore } from '../../store/appStore/appStore'
import { useViewWidgetState } from '../../store/appStore/view/useViewWidgetState'
import { useWidgetConfig } from '../WidgetRenderer/WidgetProvider'
import { useWidgetQuery } from '../widget-query/useWidgetQuery'
import { QueryControlConfig } from '../widget-types'
import { createWidget } from '../widget-utils'
import { SortEditorPopover } from './SortEditorPopover'
import { TableAutosizeAllButton } from './TableAutosizeAllButton'
import { TableStateEditorDrawer } from './TableStateEditorDrawer'
import { universalTableQueryDataSource } from './datasources'
import {
  agGridSortModelToOrderBy,
  getAgGridSortModelFromColumnState,
  orderByToAgGridSortModel,
} from './filter-converters'
import './table-styles.css'
import { columnFieldAsText } from './tableClipboard'
import {
  AGGridColDefUtils,
  ColumnMenuUtils,
  ModelFieldUtils,
  ModelStateUtils,
  SavedTableState,
  StateWithTableState,
  TableStateConfig,
  TableStateUtils,
  colDefsToExportColumns,
} from './tablewidget.parts'

export interface TableWidgetProps extends AgGridReactProps {
  queryId?: string
  caption?: string
  exportTitle?: string
  headerSlot?: ReactNode
  checkboxSelection?: any
  sortable?: boolean
  filterType?: any
  subType?: any
  paginationAfter?: any
  /**
   * Whether or not to show the old export button
   */
  showExportButton?: boolean
  showTableControls?: boolean
  showSavedViewButton?: boolean
  showSortEditor?: boolean
  showTableStateEditor?: boolean
  /**
   * Whether to show the filter editor above the table. Default true.
   * Set to false on detail views where filters are not applicable.
   */
  showFilterEditor?: boolean
  showCount?: boolean
  queryControls?: QueryControlConfig[]
  isLoading?: boolean
  tableStateConfig?: TableStateConfig
  connectionLocator?: DataTypeLocator
  height?: string | number
  style?: React.CSSProperties
}

export const TableWidget = ({
  children,
  headerSlot,
  caption,
  exportTitle,
  columnDefs,
  gridOptions,
  rowData: propsRowData,
  checkboxSelection,
  sortable,
  filterType,
  subType,
  paginationAfter,
  queryControls = [],
  height,
  queryId,
  showTableControls = false,
  showSavedViewButton = true,
  showSortEditor = true,
  showTableStateEditor = true,
  showFilterEditor = true,
  showCount = true,
  showExportButton = true,
  isLoading,
  tableStateConfig,
  connectionLocator,
  ...props
}: TableWidgetProps) => {
  assert(queryId, 'queryId is required for table to work')

  const widgetConfig = useWidgetConfig()
  const widgetId = widgetConfig?.state?.widgetId
  assert(widgetId, 'widgetId is required for table to work')

  const gridRef = useRef<AgGridReact>(null)
  const [totalCount, setTotalCount] = useState<number>(
    propsRowData?.length ?? 0,
  )
  const [loading, setLoading] = useState(true)

  const {
    savedView,
    isBaseState,
    hasRole,
    viewState,
    currentUser,
    baseViewState,
  } = useAppStore(({ viewStore, auth }) => ({
    savedView: viewStore.savedView,
    isBaseState: viewStore.isBaseState,
    hasRole: auth.hasRole,
    viewState: viewStore.viewState,
    currentUser: auth.user,
    baseViewState: viewStore.baseViewState,
  }))

  const { paginated, updateQueryInputValue, resetPagination, queryNode } =
    useWidgetQuery({
      queryId,
    })

  const listDataTypeConfig = useMemo(
    () =>
      pipe(
        queryNode.queryDataTypeConfig,
        DataTypeConfigUtils.find(connectionLocator!),
        Assert.orThrow(
          DataTypeConfigUtils.isKind('FIELD'),
          () => new Error(`Query definition not found for queryId: ${queryId}`),
        ),
        DataTypeConfigUtils.getOfType(),
        Assert.orThrow(
          DataTypeConfigUtils.isKind('OBJECT'),
          () =>
            new Error(
              `Query definition Object not found for queryId: ${queryId}`,
            ),
        ),
        DataTypeConfigUtils.getField('items'),
        DataTypeConfigUtils.getOfType(),
        Assert.orThrow(
          DataTypeConfigUtils.isKind('LIST'),
          () =>
            new Error(
              `Query definition List not found for queryId: ${queryId}`,
            ),
        ),
        TableStateUtils.constructColumnsFromObjectFields(),
        Record.deepCopy,
        // DataTypeConfigService.copySelfComputedConfig(5),
        ModelFieldUtils.removeDisabledFields(),
        ModelStateUtils.updateState(
          tableStateConfig
            ? {
                _tableColumns: tableStateConfig,
              }
            : {},
        ),
        TableStateUtils.initializeTableState(),
        TableStateUtils.setColumnConfigs(),
        TableStateUtils.sortColumnsByVisibility(),

        TableStateUtils.addMissingColumns(),
        TableStateUtils.sortByPinned(),
        TableStateUtils.filterByAvailableFields(hasRole),
      ),
    [queryNode.queryDataTypeConfig, tableStateConfig, hasRole],
  )

  const orderByInputValueContentNode = useMemo(
    () =>
      pipe(
        queryNode.queryVariableContentNodes,
        ContentNodeUtils.findContentNode([['key', 'orderBy']]),
        // Assert.orReturn(Value.isNotNullable, () => null),
        Assert.orReturn(ContentNodeUtils.isKind('INPUT_VALUE'), () => null),
      ),
    [queryNode.queryVariableContentNodes],
  )

  // Get the filter input value content node from query variables
  const filterInputValueContentNode = useMemo(
    () =>
      pipe(
        queryNode.queryVariableContentNodes,
        ContentNodeUtils.findContentNode([['key', 'filter']]),
        Assert.orReturn(Value.isNotNullable, () => null),
        Assert.orReturn(ContentNodeUtils.isKind('INPUT_VALUE'), () => null),
      ),
    [queryNode.queryVariableContentNodes],
  )

  const [tableState, setWidgetState, baseTableState] = useViewWidgetState<
    StateWithTableState,
    SavedTableState
  >({
    /** should automatically get this from context.
     * Not sure if it's worth having a fallback here for
     * anything.
     */
    widgetId: widgetId,
    persist: showTableControls, //only persist if the editorUI is enabled
    state: listDataTypeConfig.state as StateWithTableState,
    onRead: savedState => {
      const readState = pipe(
        listDataTypeConfig,
        TableStateUtils.fromSavedState(savedState), // TODO: false was isSavedView, but it broke

        ModelStateUtils.getState(),
      ) as StateWithTableState

      return readState
    },
    onWrite: state => {
      const writtenState = pipe(
        listDataTypeConfig,
        ModelStateUtils.updateState(state),
        TableStateUtils.toSavedState(listDataTypeConfig, false), // TODO: false was isSavedView, but it broke
      )

      return writtenState
    },
  })

  // final column definitions sent to AG Grid.
  const finalColumnDefs = useMemo(
    () =>
      pipe(
        listDataTypeConfig,
        ModelStateUtils.updateState(tableState),
        AGGridColDefUtils.fromModel(),
        colDefs =>
          colDefs.filter(colDef => {
            const columnConfig = colDef.context?.tableColumnConfig
            const columnRoles = columnConfig?.config?.state?.roles
            return hasRole(Array.isArray(columnRoles) ? columnRoles : [])
          }),
      ),
    [listDataTypeConfig, tableState, hasRole],
  )

  // AG Grid fires columnMoved (and similar) continuously while dragging.
  // Persist only when the interaction finishes so we don't rebuild
  // columnDefs and write URL state on every move event.
  const updateColumnsDefs = (params?: object) => {
    if (params != null && (params as any).finished === false) {
      return
    }

    const colDefs = gridRef.current?.api.getColumnDefs() ?? []

    setWidgetState({
      _tableColumns: AGGridColDefUtils.toTableState(colDefs) as any,
    })
  }

  /** Reset AG Grid's server-side pagination state */
  const resetServerSidePagination = useCallback(() => {
    if (!gridRef.current?.api) {
      return
    }
    gridRef.current.api.refreshServerSide({
      purge: true,
    })
  }, [])

  /** Only used to upgrade the Grid's internal filter/sort state. */
  const updateGridFilterSort = (shouldRefresh = true) => {
    if (!gridRef.current?.api) {
      return
    }

    // Convert orderBy from query store to AG Grid sortModel format and apply it
    const sortModel =
      effectiveOrderBy.length > 0
        ? orderByToAgGridSortModel(effectiveOrderBy)
        : []
    gridRef.current.api.applyColumnState({
      state: sortModel,
      defaultState: { sort: null },
    })

    // Only refresh server side if explicitly requested (e.g., on initial load or filter change)
    // Use setTimeout to ensure applyColumnState has been processed before refresh
    if (shouldRefresh) {
      resetServerSidePagination()
    }
  }

  const onGridReady = () => {
    updateGridFilterSort(true)
  }

  const { filter } = useMemo(() => {
    return QueryNodeUtils.getMappedVariablesAsObject()(queryNode)
  }, [queryNode])

  const effectiveOrderBy = useMemo(() => {
    if (
      orderByInputValueContentNode?.data &&
      Array.isArray(orderByInputValueContentNode.data)
    ) {
      return orderByInputValueContentNode.data as Array<
        Record<string, 'ASC' | 'DESC'>
      >
    }
    return []
  }, [orderByInputValueContentNode])

  // Only refresh on filter changes, not on orderBy changes (orderBy changes are handled by datasource)
  useOnChange(() => {
    updateGridFilterSort(true)
  }, [filter])

  useOnChange(() => {
    if (gridRef.current?.api) {
      const currentSortModel = getAgGridSortModelFromColumnState(
        gridRef.current.api.getColumnState(),
      )

      const expectedSortModel = orderByToAgGridSortModel(effectiveOrderBy)

      const sortModelsMatch = isEqual(currentSortModel, expectedSortModel)

      // Always sync from store to grid when they don't match
      if (!sortModelsMatch) {
        updateGridFilterSort(false)
      }
    }
  }, [effectiveOrderBy, orderByInputValueContentNode])

  const dataSource = useMemo(() => {
    if (!paginated) {
      return
    }

    return universalTableQueryDataSource(
      queryId!,
      setLoading,
      setTotalCount,
      connectionLocator!,
    ) as any
  }, [paginated, queryId, setLoading, setTotalCount, connectionLocator])

  const { isOpen, onOpen, onClose } = useDisclosure()

  // Handle sort changes from the table
  const handleSortChanged = useCallback(
    async (params: any) => {
      if (!orderByInputValueContentNode) return

      // Get the current sort model from AG Grid column state
      const sortModel = getAgGridSortModelFromColumnState(
        params.api.getColumnState(),
      )

      // Convert AG Grid sortModel to orderBy format
      const orderBy = agGridSortModelToOrderBy(sortModel)

      // Update the orderByInputValueContentNode
      const updatedOrderByNode = pipe(
        orderByInputValueContentNode,
        ContentNodeUtils.updateContentNode(orderBy),
      )

      // Reset pagination and update the query before refreshing server-side rows
      resetPagination()
      await updateQueryInputValue(updatedOrderByNode)

      // Reset AG Grid's server-side pagination state
      resetServerSidePagination()
    },
    [
      orderByInputValueContentNode,
      resetPagination,
      updateQueryInputValue,
      resetServerSidePagination,
    ],
  )

  // TODO: this needs to get cleaned up. figure out how to get the render without reprocessing the cell output
  const getContextMenuItems = useCallback<GetContextMenuItems<unknown>>(
    params => {
      // Extract the field content node from the row data using the full selector path
      const rowData = params.node?.data as ObjectContentNode
      const colDef = params.column?.getColDef()
      const columnConfig = colDef?.context?.tableColumnConfig

      // Use the column's selector to navigate to the correct nested field
      let fieldContentNode: FieldContentNode | undefined | null
      if (columnConfig?.selector) {
        // Use ContentNodeUtils to follow the selector path (e.g., TransportUnitShipmentDocument -> TransportUnit -> transportUnitId)
        fieldContentNode = ContentNodeUtils.findContentNode(
          columnConfig.selector,
        )(rowData) as FieldContentNode | null
      } else {
        // Fallback to simple field lookup
        const fieldKey = colDef?.field
        fieldContentNode = rowData?.fields?.find(f => f.key === fieldKey)
      }

      const url = fieldContentNode?.state?.linkUrl

      const result: (DefaultMenuItem | MenuItemDef<any, any>)[] = [
        ...(url
          ? [
              {
                name: 'Open Link In New Tab',
                icon: '<svg viewBox="0 0 24 24" focusable="false"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><path d="M15 3h6v6"></path><path d="M10 14L21 3"></path></g></svg>',
                action: () => {
                  window.open(url, '_blank')
                },
              },
              'separator' as const,
            ]
          : []),
        {
          name: 'Copy',
          icon: `<i class="ag-icon ag-icon-copy"/>`,
          action: () =>
            navigator.clipboard.writeText(
              columnFieldAsText(columnConfig)(rowData),
            ),
        },
        {
          name: 'Copy Row With Headers',
          icon: `<i class="ag-icon ag-icon-copy"/>`,
          action: () => {
            const rowContentNode = params.node?.data as ObjectContentNode
            const columnApi = params.api

            // Get all visible columns in their display order
            const visibleColumns = columnApi.getAllDisplayedColumns()

            // Extract field values with headers for each visible column
            const valuesWithHeaders = visibleColumns.map(column => {
              const colDef = column.getColDef()
              const columnConfig = colDef?.context?.tableColumnConfig
              const label = (colDef.headerName || colDef.field || '').toString()

              const textValue = columnFieldAsText(columnConfig)(rowContentNode)

              return `${label}: ${textValue}`
            })

            navigator.clipboard.writeText(valuesWithHeaders.join('\t'))
          },
        },
      ].filter(Boolean)

      return result
    },
    [],
  )
  const showTitle = !!caption
  const shouldShowTableControls = showTableControls
  const shouldShowExportButton = showExportButton && !shouldShowTableControls
  const showRecords = showCount && totalCount >= 0

  const showHeaderSlot = !!headerSlot
  const showQueryControls = queryControls.length > 0

  const showHeading =
    showHeaderSlot ||
    showTitle ||
    shouldShowExportButton ||
    showQueryControls ||
    showRecords ||
    shouldShowTableControls

  const { style = {}, ...rootElementProps } = props
  const { borderRadius, ...rootElementStyle } = style

  const {
    isOpen: isSaveViewModalopen,
    onClose: closeSaveViewModal,
    onOpen: openSaveViewModal,
  } = useDisclosure({})

  // Process exportTitle if it's a runtime operation (like STRING_BUILDER with CONTENT_NODE_REFERENCE)
  const processedExportTitle = useMemo(() => {
    if (!exportTitle || typeof exportTitle === 'string') {
      return exportTitle
    }

    try {
      const allQueries = useAppStore.getState().queryStore.queries

      // Don't process until we have query data
      if (!queryNode?.queryContentNode) {
        return undefined
      }

      // Context needs a query function, matching WidgetRenderer pattern
      const context = {
        query: (queryId: string) => allQueries[queryId],
      }

      // Step 1: Process runtime config (resolves CONTENT_NODE_REFERENCE)
      const processed = RuntimeOperations.processValue(
        context as RuntimeOperationBaseContext,
      )(exportTitle)

      if (!processed) {
        return undefined
      }

      // Step 2: Process runtime content config node (resolves STRING_BUILDER)
      const processedNode = RuntimeOperations.processContentNode({
        context: context as RuntimeOperationBaseContext,
      })(processed as any)

      if (!processedNode) {
        return undefined
      }

      // Helper function to deeply extract scalar data from content nodes
      const extractData = (item: any): any => {
        if (!item || typeof item !== 'object') {
          return item
        }

        // Handle different content node types
        if (item.kind === 'FIELD' && item.ofType) {
          // FIELD wraps the actual value in ofType
          return extractData(item.ofType)
        }

        if (
          item.kind === 'LIST' &&
          Array.isArray(item.items) &&
          item.items.length > 0
        ) {
          // For LIST, get the first item
          return extractData(item.items[0])
        }

        if (item.kind === 'SCALAR' && 'data' in item) {
          // SCALAR has the actual data
          return item.data
        }

        // Fallback: try to get data property
        if ('data' in item) {
          return item.data
        }

        return item
      }

      // Extract the final string value
      let result = processedNode

      // If it's still a content node, extract the data value
      if (result && typeof result === 'object' && !Array.isArray(result)) {
        result = extractData(result)
      }

      // Handle arrays (in case STRING_BUILDER returned an array with content nodes)
      if (Array.isArray(result)) {
        result = result
          .map(item => extractData(item))
          .filter(item => item != null && item !== '') // Filter out null/undefined/empty
          .join('')
      }

      return typeof result === 'string' ? result : undefined
    } catch (e) {
      console.error('[ExportTitle] Error processing:', e)
      return undefined
    }
  }, [exportTitle, queryNode?.queryContentNode])

  const Header = (
    <VStack>
      {showTitle && (
        <HStack alignItems="center" gap="16px">
          <Heading variant="pageTitle" m="0">
            {caption}
          </Heading>

          {savedView && (
            <Box
              color="presets.turquoise.60"
              fontWeight="600"
              fontSize="12px"
              px="10px"
              py="4px"
              border="1px solid"
              borderColor="presets.turquoise.50"
              borderRadius="4px"
              backgroundColor="presets.turquoise.10"
            >
              {savedView?.name}
            </Box>
          )}
        </HStack>
      )}
    </VStack>
  )

  const getHeaderName = useCallback(
    (columnId: string): string => {
      const columnConfig =
        listDataTypeConfig.state._tableColumns.columnConfigs.find(
          columnConfig => columnConfig.key === columnId,
        )

      if (columnConfig?.config?.state.label)
        return columnConfig?.config?.state.label as string

      return columnId
    },
    [listDataTypeConfig],
  )

  // --- Column-header filtering -------------------------------------------------
  // The column-header filter reuses the shared filter components and edits the
  // same filter content node as the main FilterEditor, so applied column filters
  // surface as standard pills/rows in the main filter modal.
  const filterObjectDataTypeConfig = useMemo(() => {
    if (!filterInputValueContentNode) return undefined
    try {
      return getFilterObjectContentNode(
        filterInputValueContentNode,
      ).getDataTypeConfig() as InputObjectDataTypeConfig
    } catch {
      return undefined
    }
  }, [filterInputValueContentNode])

  const availableFilterFields = useMemo(
    () =>
      filterObjectDataTypeConfig
        ? getAvailableFilterFieldsDataTypeConfigs(
            filterObjectDataTypeConfig,
            hasRole,
          )
        : [],
    [filterObjectDataTypeConfig, hasRole],
  )

  const filterObjectTypeName = filterObjectDataTypeConfig?.typeName
  const unmappedFilterTypeName = getUnmappedTypeName(
    filterObjectDataTypeConfig as Parameters<typeof getUnmappedTypeName>[0],
  )

  // Mirrors the FilterEditor's distinct-values loader so the column-header
  // "Is Any Of" pick list resolves real options (not mock data).
  const columnLoadOptionsForPickList = useMemo(() => {
    const distinctLoadOptions = createDistinctValuesLoadOptions()
    const cubeDistinctLoadOptions = CubeDistinctValues.createLoadOptions()
    return (params: LoadOptionsForPickListParams) => {
      const fieldName = params.unmappedFieldKey ?? ''
      if (params.cubeDistinctView && fieldName) {
        return cubeDistinctLoadOptions({
          ...params,
          unmappedFieldKey: fieldName,
        })
      }
      const typeName =
        params.unmappedTypeName ??
        unmappedFilterTypeName ??
        filterObjectTypeName ??
        ''
      if (!typeName || !fieldName) return Promise.resolve([])
      return distinctLoadOptions({
        ...params,
        unmappedTypeName: typeName,
        unmappedFieldKey: fieldName,
      })
    }
  }, [unmappedFilterTypeName, filterObjectTypeName])

  const applyFilterChange = useCallback(
    (updatedInputValueContentNode: InputValueContentNode) => {
      resetPagination()
      updateQueryInputValue(updatedInputValueContentNode)
      resetServerSidePagination()
    },
    [resetPagination, updateQueryInputValue, resetServerSidePagination],
  )

  const [openColumnFilterField, setOpenColumnFilterField] = useState<
    string | null
  >(null)

  // Serialized menu revision for AG Grid header refresh. See ColumnMenuUtils.
  const columnMenuRevision = useMemo(() => {
    if (!filterInputValueContentNode) return ''
    try {
      const data = getFilterObjectContentNode(filterInputValueContentNode).data
      return ColumnMenuUtils.buildColumnMenuRevision({
        filterData: (data ?? {}) as Record<string, unknown>,
        filterFieldKeys: availableFilterFields.map(config => config.key),
        colorMode: widgetConfig?.state.colorMode ?? null,
        openColumnFilterField,
      })
    } catch {
      return ''
    }
  }, [
    filterInputValueContentNode,
    availableFilterFields,
    widgetConfig?.state.colorMode,
    openColumnFilterField,
  ])

  const renderColumnMenu = useCallback<RenderColumnMenu>(
    ({ field }) => {
      if (!filterInputValueContentNode || !field) return null
      const fieldConfig = availableFilterFields.find(
        config => config.key === field,
      )
      if (!fieldConfig) return null
      return (
        <ColumnFilterEditor
          fieldKey={field}
          label={getHeaderName(field)}
          filterInputValue={filterInputValueContentNode}
          availableFilters={availableFilterFields}
          loadOptionsForPickList={columnLoadOptionsForPickList}
          colorMode={widgetConfig?.state.colorMode}
          onChange={applyFilterChange}
          isPopoverOpen={openColumnFilterField === field}
          onPopoverOpenChange={isOpen =>
            setOpenColumnFilterField(isOpen ? field : null)
          }
        />
      )
    },
    [
      filterInputValueContentNode,
      availableFilterFields,
      columnLoadOptionsForPickList,
      widgetConfig?.state.colorMode,
      applyFilterChange,
      getHeaderName,
      openColumnFilterField,
    ],
  )

  const Records = (
    <Text
      color="presets.turquoise.100"
      fontSize="12px"
      fontWeight="600"
      lineHeight="14.63px"
    >
      <InlineSkeleton isLoading={loading && !totalCount}>
        {totalCount >= 0 && numberFormatter(totalCount)} records
      </InlineSkeleton>
    </Text>
  )

  const isSavedView = Boolean(savedView)

  // Check if current user has access to the saved view:
  // - User is the owner, OR
  // - User shares the same organization as the saved view
  const hasSavedViewAccess =
    savedView && currentUser?.id
      ? savedView.userId === currentUser.id ||
        savedView.organizationId === currentUser.organization?.id
      : false
  // const isSavedViewBtnDisabled = isBaseState && !(isSavedView && !isSavedViewOwner)
  const isSavedViewSharable = isSavedView && !hasSavedViewAccess
  const isSavedViewBtnDisabled =
    isBaseState && (!isSavedView || isSavedViewSharable)

  const autoSizeGridColumn = useCallback((columnKey: string) => {
    gridRef.current?.api?.autoSizeColumns([columnKey])
  }, [])

  const autoSizeAllVisibleGridColumns = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) return
    const keys = TableStateUtils.autosizeAllVisibleColumnKeys(
      tableState._tableColumns.columns,
    )
    if (keys.length === 0) return
    api.autoSizeColumns(keys)
  }, [tableState._tableColumns.columns])

  const controlSkeletonProps = {
    isLoading: true,
    height: '32px',
    rounded: '4px',
  } as const

  const TableControls = loading ? (
    <Flex justifyContent="flex-end" alignItems="end" gap="8px">
      {orderByInputValueContentNode && showSortEditor && (
        <Skeleton {...controlSkeletonProps} width="160px" />
      )}
      <Skeleton {...controlSkeletonProps} width="32px" />
      {showSavedViewButton && (
        <Skeleton {...controlSkeletonProps} width="32px" />
      )}
      <Skeleton {...controlSkeletonProps} width="32px" />
      {showTableStateEditor && (
        <Skeleton {...controlSkeletonProps} width="32px" />
      )}
    </Flex>
  ) : (
    <Flex justifyContent="flex-end" alignItems="end" gap="8px">
      {orderByInputValueContentNode && showSortEditor && (
        <ErrorBoundary renderError={() => <WidgetError />}>
          {/* onChange will be added later */}
          <SortEditorPopover
            orderByInputValueContentNode={orderByInputValueContentNode}
            onChange={updatedInputValueContentNode => {
              resetPagination()
              //TODO:  the update variable one turned into an infinite loop. This works though. Investigate later.
              updateQueryInputValue(updatedInputValueContentNode)

              // Reset AG Grid's server-side pagination state
              resetServerSidePagination()
            }}
          />
        </ErrorBoundary>
      )}
      <TableAutosizeAllButton onClick={autoSizeAllVisibleGridColumns} />
      {showSavedViewButton && (
        <>
          <Tooltip label="Save View" placement="top">
            <Box position="relative">
              <IconButton
                variant="controlButton"
                aria-label="Save View"
                fontSize="20px"
                icon={<SaveIcon m="-4px" />}
                isDisabled={isSavedViewBtnDisabled}
                onClick={openSaveViewModal}
              />
            </Box>
          </Tooltip>
          <SaveViewModal
            isOpen={isSaveViewModalopen}
            onClose={closeSaveViewModal}
            columns={tableState._tableColumns.columns}
            columnConfigs={tableState._tableColumns.columnConfigs}
            connectionLocator={connectionLocator}
            queryId={queryId}
            widgetId={widgetId}
            children={<></>}
          />
        </>
      )}
      <ExportModal
        queryId={queryId}
        title={caption}
        exportTitle={savedView?.name || processedExportTitle}
        columns={tableState._tableColumns.columns}
        columnConfigs={tableState._tableColumns.columnConfigs}
        connectionLocator={connectionLocator}
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        queryVariables={pipe(
          queryNode,
          QueryNodeUtils.getMappedVariablesAsObject(),
        )}
        queryDefinitionId={queryNode.queryDefinition?.id}
        renderTrigger={
          <IconButton
            variant="controlButton"
            aria-label="Export"
            icon={<Icon icon="DownloadIcon" boxSize="16px" m="-2px" />}
            onClick={onOpen}
          />
        }
      />

      {showTableStateEditor && (
        <ErrorBoundary renderError={() => <WidgetError />}>
          <TableStateEditorDrawer
            listDatatypeConfig={listDataTypeConfig}
            value={tableState._tableColumns.columns}
            defaultValue={
              baseTableState?._tableColumns?.columns ??
              listDataTypeConfig.state._tableColumns.columns
            }
            onChange={columns => {
              setWidgetState({
                _tableColumns: {
                  ...tableState._tableColumns,
                  columns,
                },
              })
            }}
          />
        </ErrorBoundary>
      )}
    </Flex>
  )

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex="1"
      {...rootElementProps}
      style={rootElementStyle}
    >
      <VStack alignItems="stretch" py="16px">
        {showFilterEditor &&
          filterInputValueContentNode &&
          (loading ? (
            <Skeleton isLoading height="32px" width="84px" rounded="4px" />
          ) : (
            <ErrorBoundary renderError={() => <WidgetError />}>
              <FilterEditor
                inputValue={filterInputValueContentNode}
                onChange={updatedInputValueContentNode => {
                  resetPagination()
                  //TODO:  the update variable one turned into an infinite loop. This works though. Investigate later.
                  updateQueryInputValue(updatedInputValueContentNode)

                  // Reset AG Grid's server-side pagination state
                  resetServerSidePagination()
                }}
                colorMode={widgetConfig?.state.colorMode}
              />
            </ErrorBoundary>
          ))}
        {showHeaderSlot && (
          <VStack alignItems="flex-start">{headerSlot}</VStack>
        )}
        <Flex
          justify={showRecords ? 'space-between' : 'flex-end'}
          alignItems="center"
        >
          {showRecords && Records}
          {shouldShowTableControls && TableControls}
        </Flex>
      </VStack>
      <Table
        flex="1"
        ref={gridRef as any}
        onGridReady={onGridReady}
        rowData={paginated ? null : propsRowData || null}
        getContextMenuItems={getContextMenuItems}
        renderColumnMenu={renderColumnMenu}
        columnMenuRevision={columnMenuRevision}
        onAutoSizeColumn={autoSizeGridColumn}
        onColumnPinned={updateColumnsDefs}
        height={height}
        borderRadius={borderRadius}
        onColumnVisible={updateColumnsDefs}
        onColumnMoved={updateColumnsDefs}
        columnDefs={finalColumnDefs}
        serverSideDatasource={dataSource}
        rowModelType={paginated ? 'serverSide' : 'clientSide'}
        gridOptions={gridOptions as any}
        loading={loading}
        onFirstDataRendered={() => setLoading(false)}
        onSortChanged={handleSortChanged}
      />
    </Box>
  )
}

export const TableWidgetDefinition = createWidget(TableWidget, {
  type: 'Table',
  manualQuery: true,
  slots: ['headerSlot'],
})

const findInList = <T, K extends keyof T>(
  list: T[],
  property: K,
  value: string,
): T | undefined => list.find(item => item[property] === value)
